<template>
  <v-dialog
    v-model="formState"
    :max-width="800"
    :fullscreen="isMobile"
    scrollable
    :transition="isMobile?'dialog-bottom-transition':'fade-transition'"
  >
    <v-card class="pa-2">
      <v-card-title>{{ formTitle }}</v-card-title>
      
      <v-card-text>
        <div v-if="loading">Cargando contactos...</div>
        <div v-else>
          <v-list>
            <v-list-item v-for="contact in contactsList" :key="contact.id">
              <v-checkbox
                v-model="selectedContactIds"
                :label="contact.name + (contact.email ? ' - ' + contact.email : '')"
                :value="contact.id"
                density="compact"
              />
              <template #append>
                <span v-if="contact.phone">📱 {{ contact.phone }}</span>
              </template>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-btn variant="text" color="red" @click="close">Cancelar</v-btn>
        <v-spacer/>
        <!-- EMAIL BUTTON WITH TOOLTIP -->
        <v-tooltip v-if="selectedContacts.length && !canSendEmail" location="top">
          <template #activator="{ props }">
            <span v-bind="props">
              <v-btn color="success" :disabled="!canSendEmail" @click="sendEmail">
                Enviar Email
              </v-btn>
            </span>
          </template>
          <span>
            Los siguientes contactos no tienen email:<br>
            <span v-for="c in missingEmailContacts" :key="c.id">
              {{ c.name || c.email || c.phone }}
            </span>
          </span>
        </v-tooltip>
        <v-btn v-else color="success" :disabled="selectedContacts.length === 0" @click="sendEmail">
          Enviar Email
        </v-btn>

        <!-- WHATSAPP BUTTON WITH TOOLTIP -->
        <v-tooltip v-if="selectedContacts.length && !canSendWhatsapp" location="top">
          <template #activator="{ props }">
            <span v-bind="props">
              <v-btn color="primary" :disabled="!canSendWhatsapp" @click="sendWhatsapp">
                Enviar WhatsApp
              </v-btn>
            </span>
          </template>
          <span>
            Los siguientes contactos no tienen WhatsApp:<br>
            <span v-for="c in missingWhatsappContacts" :key="c.id">
              {{ c.name || c.email || c.phone }}
            </span>
          </span>
        </v-tooltip>
        <v-btn v-else color="primary" :disabled="selectedContacts.length === 0" @click="sendWhatsapp">
          Enviar WhatsApp
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted } from "vue";
import { useSendFileDialogStore } from "@/store/sendFileDialog";
import { useContactsStore } from "@/store/contacts";
import { useDisplay } from 'vuetify';

const sendFileDialogStore = useSendFileDialogStore();
const contactsStore = useContactsStore();

const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);

const formTitle = computed(() => sendFileDialogStore.formTitle);
const client_id = computed(() => sendFileDialogStore.client_id);

const formState = computed(() => sendFileDialogStore.isOpen);

const loading = ref(false);
const contactsList = ref<any[]>([]);
const selectedContactIds = ref<any[]>([]);

const selectedContacts = computed(() =>
  contactsList.value.filter(c => selectedContactIds.value.includes(c.id))
);

const fetchContacts = async () => {
  loading.value = true;
  contactsList.value = [];
  if (client_id.value) {
    await contactsStore.fetchContactByClientId(Number(client_id.value));
    // Agrega el main_email si lo tienes
    if (sendFileDialogStore.main_email) {
      contactsList.value = [
        {
          id: 'main',
          name: 'Email Principal',
          email: sendFileDialogStore.main_email,
        },
        ...contactsStore.contacts, // debe tener id, name, email, whatsapp
      ];
    } else {
      contactsList.value = contactsStore.contacts;
    }
  }
  loading.value = false;
};

watch(formState, (open) => {
  if (open) fetchContacts();
});

const sendEmail = async () => {
  if (typeof sendFileDialogStore.onSendEmail === 'function') {
    await sendFileDialogStore.onSendEmail(selectedContacts.value);
    sendFileDialogStore.closeForm();
  }
};

const sendWhatsapp = async () => {
  if (typeof sendFileDialogStore.onSendWhatsapp === 'function') {
    await sendFileDialogStore.onSendWhatsapp(selectedContacts.value);
    sendFileDialogStore.closeForm();
  }
};

const close = () => sendFileDialogStore.closeForm();

const missingEmailContacts = computed(() =>
  selectedContacts.value.filter(c => !c.email)
);
const missingWhatsappContacts = computed(() =>
  selectedContacts.value.filter(c => !c.phone && !c.whatsapp)
);

const canSendEmail = computed(() =>
  selectedContacts.value.length > 0 && missingEmailContacts.value.length === 0
);
const canSendWhatsapp = computed(() =>
  selectedContacts.value.length > 0 && missingWhatsappContacts.value.length === 0
);
</script>
